
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, Users, Calendar, Coins, Settings, Package, PieChart, CreditCard } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAppConfig } from '@/contexts/AppConfigContext';
import { useUserProfile } from '@/contexts/UserProfileContext';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '@/hooks/use-mobile';
import { Badge } from '@/components/ui/badge';
import { usePlanLimits } from '@/hooks/usePlanLimits';
import { useProModal } from '@/contexts/ProModalContext';
import { useToast } from '@/hooks/use-toast';
import { Share2, Copy, Check, ExternalLink, User } from 'lucide-react';
import ProGate from '@/components/Guards/ProGate';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';


interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const location = useLocation();
  const { logoUrl } = useAppConfig();
  const { t } = useTranslation();
  const { userProfile } = useUserProfile();
  const { userRole } = useAuth();
  const isMobile = useIsMobile();
  const { limits } = usePlanLimits();
  const { openProModal, openSubscriptionModal } = useProModal();

  const getPlanLabel = () => {
    if (userRole === 'admin' || userRole === 'superadmin') return 'ADMIN';
    const plan = userProfile?.subscriptionPlan?.toLowerCase() || '';
    if (plan === 'pro' || plan === 'enterprise') return 'PRO';
    return 'FREE';
  };

  const { toast } = useToast();
  const [copied, setCopied] = React.useState(false);
  const { currentUser } = useAuth();
  
  const publicQuotationUrl = currentUser ? `${window.location.origin}/q/${currentUser.uid}` : '';

  const handleCopyUrl = () => {
    if (publicQuotationUrl) {
      navigator.clipboard.writeText(publicQuotationUrl);
      setCopied(true);
      toast({ title: t('quotation.copy_success') || '¡Enlace copiado!' });
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const navigation = [
    { name: t('nav.dashboard'), href: '/', icon: Home },
    { name: t('nav.sales', 'Ventas'), href: '/sales', icon: Coins },
    { name: t('nav.events'), href: '/events', icon: Calendar },
    { name: t('nav.customers'), href: '/customers', icon: Users },
    { name: t('nav.payments'), href: '/payments', icon: PieChart },
    { name: t('nav.inventory'), href: '/inventory', icon: Package },
  ];

  return (
    <>
      {/* Mobile overlay */}
      {isMobile && isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={onClose}
        />
      )}
      
      <div className={cn(
        "fixed top-0 left-0 h-full bg-[#111c43] text-white transition-transform duration-300 ease-in-out z-50 shadow-xl",
        isMobile ? "w-64" : "w-64",
        isOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        <div className="flex flex-col h-full">
          {/* Header / Logo */}
          <div className="flex items-center px-6 py-4 h-[72px] border-b border-white/10">
            {logoUrl ? (
              <img 
                src={logoUrl} 
                alt="Logo" 
                className="h-8 w-auto max-w-[150px] brightness-0 invert" // Make logo white if possible
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = 'none';
                }}
              />
            ) : (
              <div className="flex items-center justify-center w-full">
                <img 
                  src="/logo.png" 
                  alt="BASS Line Logo" 
                  className="h-8 w-auto max-w-[150px] brightness-0 invert" 
                />
              </div>
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-1">
            <div className="px-3 mb-2 text-xs font-semibold text-white/40 uppercase tracking-wider">
              Main
            </div>
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              const isLocked = item.href === '/payments' && !limits.canAccessFinances;
              return (
                <Link
                  key={item.name}
                  to={isLocked ? '#' : item.href}
                  onClick={(e) => {
                    if (isLocked) {
                      e.preventDefault();
                      openProModal('Módulo de Finanzas', "El módulo de Finanzas es exclusivo para planes Pro o Enterprise.");
                      return;
                    }
                    if (isMobile) onClose();
                  }}
                  className={cn(
                    "flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200",
                    isActive
                      ? 'bg-white/10 text-white font-semibold'
                      : 'text-white/60 hover:bg-white/5 hover:text-white',
                    isLocked && 'opacity-60 cursor-not-allowed'
                  )}
                >
                  <item.icon className={cn("mr-3 h-5 w-5", isActive ? "text-white" : "text-white/50", isLocked && 'text-amber-500/70')} />
                  {item.name}
                  {isLocked && (
                    <span className="ml-auto text-[10px] bg-amber-500/20 text-amber-500 px-1.5 py-0.5 rounded font-bold uppercase tracking-widest border border-amber-500/20">
                      Pro
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>

            <div className="px-4 py-4 mt-auto border-t border-white/10 space-y-3">

              {/* Public URL Box */}
              <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                <div className="flex items-center gap-2 mb-2">
                  <Share2 className="h-4 w-4 text-purple-400" />
                  <span className="text-xs font-semibold text-white/90 leading-tight">URL de Reservas</span>
                </div>
                <ProGate featureName={t('limits.public_url_pro_only')}>
                  <div className="flex gap-1.5 mt-2">
                    <Button 
                      size="sm" 
                      variant="secondary" 
                      onClick={handleCopyUrl}
                      className="h-7 px-2 flex-1 text-[10px] bg-white/10 hover:bg-white/20 text-white border-0"
                    >
                      {copied ? <Check className="h-3 w-3 mr-1 text-green-400" /> : <Copy className="h-3 w-3 mr-1" />}
                      Copiar
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => currentUser && window.open(`${window.location.origin}/q/${currentUser.uid}`, '_blank')}
                      className="h-7 w-7 p-0 bg-white/10 hover:bg-white/20 text-white border-0 shrink-0"
                    >
                      <ExternalLink className="h-3 w-3" />
                    </Button>
                  </div>
                </ProGate>
              </div>

              {/* Profile / Settings */}
              <Link
                to="/settings"
                onClick={() => { if (isMobile) onClose(); }}
                className="w-full flex items-center px-3 py-2.5 text-sm font-medium rounded-lg text-white/90 hover:bg-white/10 transition-all duration-200"
              >
                <div className="bg-purple-500/20 p-1.5 rounded-md mr-3">
                  <User className="h-4 w-4 text-purple-300" />
                </div>
                <div className="flex flex-col items-start overflow-hidden">
                  <span className="truncate w-full text-left font-semibold">{userProfile?.artistName || 'Configuración'}</span>
                  <span className="text-[10px] text-white/50 truncate w-full text-left">{currentUser?.email}</span>
                </div>
              </Link>

              {/* Subscription */}
              <button
                onClick={openSubscriptionModal}
                className="w-full flex items-center px-3 py-2.5 text-sm font-medium rounded-lg text-white/60 hover:bg-white/5 hover:text-white transition-all duration-200 cursor-pointer"
              >
                <CreditCard className="mr-3 h-5 w-5 text-white/50" />
                Suscripción
                <Badge variant="outline" className="ml-auto text-[10px] border-white/20 text-white/80 bg-white/10 hover:bg-white/20 uppercase">
                  {getPlanLabel()}
                </Badge>
              </button>
            </div>

          {/* Logo at bottom */}
          {logoUrl && (
            <div className="px-6 py-4 border-t border-border">
              <div className="flex items-center justify-center">
                <img 
                  src={logoUrl} 
                  alt="User Logo" 
                  className="h-12 w-auto max-w-[180px]"
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).style.display = 'none';
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Sidebar;
